/**
 * PB-BRI-1 — Per-line AI media ownership helpers.
 * Anchor/main may first-wave gap-fill blank siblings; secondary edits never fan Character/Photo.
 * Pure functions for Node tests + browser (window.PBMediaLineOwnership).
 */
(function (global) {
  'use strict';

  var AI_MEDIA_TIER1_KEYS = ['Photo', 'Character'];

  function normalizeLabel(label) {
    return String(label == null ? '' : label)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function isEmptyValue(val) {
    if (val == null || val === '') return true;
    if (typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val).length === 0;
    }
    return false;
  }

  function looksLikeMediaUrl(val) {
    if (isEmptyValue(val)) return false;
    var s = String(val).trim();
    if (!s || s.toLowerCase() === 'photo' || s.toLowerCase() === 'character') return false;
    if (/^https?:\/\//i.test(s)) return true;
    if (s.indexOf('/') !== -1 && s.length > 4) return true;
    return false;
  }

  function isAnchorBundleRow(bundle, mainProduct, rowIdx) {
    var idx = Number(rowIdx);
    if (!bundle || !mainProduct || Number.isNaN(idx) || idx < 0) return false;
    var items = bundle.items || [];
    var firstMainIdx = -1;
    for (var i = 0; i < items.length; i += 1) {
      if (String(items[i] && items[i].id) === String(mainProduct.id)) {
        firstMainIdx = i;
        break;
      }
    }
    var item = items[idx];
    return idx === firstMainIdx && item && String(item.id) === String(mainProduct.id);
  }

  /**
   * Only the offer anchor (main product line) may fan AI media onto matching blank lines.
   * A secondary line’s Character/Photo save stays on that line only.
   */
  function canSourceFanOutAiMedia(params) {
    params = params || {};
    if (params.allowMediaFanOut === false) return false;
    if (params.allowMediaFanOut === true) return true;
    var bundle = params.bundle;
    var mainProduct = params.mainProduct;
    var sourceIdx = params.sourceIdx;
    if (mainProduct && bundle) {
      return isAnchorBundleRow(bundle, mainProduct, sourceIdx);
    }
    /* Tests / callers without mainProduct: treat index 0 as first-wave source only. */
    return Number(sourceIdx) === 0;
  }

  function getPropByNormalizedKey(props, key) {
    if (!props || typeof props !== 'object') return undefined;
    if (Object.prototype.hasOwnProperty.call(props, key) && !isEmptyValue(props[key])) {
      return props[key];
    }
    var want = normalizeLabel(key);
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i += 1) {
      if (normalizeLabel(keys[i]) === want && !isEmptyValue(props[keys[i]])) {
        return props[keys[i]];
      }
    }
    return undefined;
  }

  function isOwnedMediaValue(key, val) {
    if (isEmptyValue(val)) return false;
    var k = normalizeLabel(key);
    if (k === 'photo' || k === 'character' || k.indexOf('photo') !== -1 || k.indexOf('character') !== -1) {
      return looksLikeMediaUrl(val);
    }
    return looksLikeMediaUrl(val);
  }

  /**
   * Gap-fill only missing/invalid media keys — never overwrite a sibling’s owned URL.
   * @returns {{ props: object, changed: boolean, filledKeys: string[] }}
   */
  function gapFillOwnedAiMedia(existingProps, mediaTier1) {
    var existing = existingProps && typeof existingProps === 'object' ? { ...existingProps } : {};
    var incoming = mediaTier1 && typeof mediaTier1 === 'object' ? mediaTier1 : {};
    var filledKeys = [];
    var keys = Object.keys(incoming);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var current = getPropByNormalizedKey(existing, key);
      if (isOwnedMediaValue(key, current)) continue;
      existing[key] = incoming[key];
      filledKeys.push(key);
    }
    return {
      props: existing,
      changed: filledKeys.length > 0,
      filledKeys: filledKeys,
    };
  }

  function targetNeedsOwnedAiMediaGapFill(slotProps, mediaTier1) {
    var fill = gapFillOwnedAiMedia(slotProps, mediaTier1);
    return fill.changed;
  }

  /**
   * Text-only (or partial) saves must not wipe Photo/Character already owned on that line.
   */
  function mergeSavedPropsPreservingOwnedMedia(existingProps, savedProperties) {
    var saved = savedProperties && typeof savedProperties === 'object' ? { ...savedProperties } : {};
    var existing = existingProps && typeof existingProps === 'object' ? existingProps : {};
    var keys = Object.keys(existing);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      if (!isOwnedMediaValue(key, existing[key])) continue;
      var incoming = getPropByNormalizedKey(saved, key);
      if (!isOwnedMediaValue(key, incoming)) {
        saved[key] = existing[key];
      }
    }
    /* Ensure canonical Photo/Character survive even if saved used different casing. */
    for (var j = 0; j < AI_MEDIA_TIER1_KEYS.length; j += 1) {
      var canon = AI_MEDIA_TIER1_KEYS[j];
      var owned = getPropByNormalizedKey(existing, canon);
      if (!isOwnedMediaValue(canon, owned)) continue;
      if (!isOwnedMediaValue(canon, getPropByNormalizedKey(saved, canon))) {
        saved[canon] = owned;
      }
    }
    return saved;
  }

  /**
   * Strip AI media keys from a sync payload when the source must not fan media.
   */
  function stripAiMediaTier1FromProps(props) {
    if (!props || typeof props !== 'object') return props || {};
    var out = { ...props };
    var keys = Object.keys(out);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var norm = normalizeLabel(key);
      if (norm === 'photo' || norm === 'character') {
        delete out[key];
      }
    }
    return out;
  }

  var api = {
    AI_MEDIA_TIER1_KEYS: AI_MEDIA_TIER1_KEYS,
    isAnchorBundleRow: isAnchorBundleRow,
    canSourceFanOutAiMedia: canSourceFanOutAiMedia,
    gapFillOwnedAiMedia: gapFillOwnedAiMedia,
    targetNeedsOwnedAiMediaGapFill: targetNeedsOwnedAiMediaGapFill,
    mergeSavedPropsPreservingOwnedMedia: mergeSavedPropsPreservingOwnedMedia,
    stripAiMediaTier1FromProps: stripAiMediaTier1FromProps,
    isOwnedMediaValue: isOwnedMediaValue,
  };

  global.PBMediaLineOwnership = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
